type FacebookLoginResponse = {
  authResponse?: {
    code?: string;
    accessToken?: string;
  };
  status?: string;
};

type FacebookLoginOptions = {
  config_id: string;
  response_type: string;
  override_default_response_type: boolean;
  extras: {
    setup: Record<string, unknown>;
    sessionInfoVersion: string;
    featureType?: string;
  };
};

declare global {
  interface Window {
    FB?: {
      init: (params: {
        appId: string;
        cookie?: boolean;
        xfbml?: boolean;
        version: string;
      }) => void;
      login: (
        callback: (response: FacebookLoginResponse) => void,
        options: FacebookLoginOptions
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

let sdkLoader: Promise<void> | null = null;
let initializedAppId: string | null = null;

export const loadFacebookSdk = (appId: string, version: string = 'v25.0'): Promise<void> => {
  if (window.FB && initializedAppId === appId) {
    return Promise.resolve();
  }

  if (window.FB) {
    window.FB.init({
      appId,
      cookie: true,
      xfbml: false,
      version
    });
    initializedAppId = appId;
    return Promise.resolve();
  }

  if (sdkLoader) {
    return sdkLoader.then(() => {
      if (window.FB && initializedAppId !== appId) {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: false,
          version
        });
        initializedAppId = appId;
      }
    });
  }

  sdkLoader = new Promise((resolve, reject) => {
    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error('SDK da Meta carregou sem o objeto FB'));
        return;
      }

      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version
      });
      initializedAppId = appId;
      resolve();
    };

    const initIfReady = () => {
      if (!window.FB) {
        return false;
      }

      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version
      });
      initializedAppId = appId;
      resolve();
      return true;
    };

    if (initIfReady()) {
      return;
    }

    if (document.getElementById('facebook-jssdk')) {
      const intervalId = window.setInterval(() => {
        if (initIfReady()) {
          window.clearInterval(intervalId);
        }
      }, 50);

      window.setTimeout(() => {
        window.clearInterval(intervalId);
        if (!initializedAppId) {
          sdkLoader = null;
          reject(new Error('Tempo esgotado ao carregar o SDK da Meta'));
        }
      }, 15000);
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
    script.onerror = () => {
      sdkLoader = null;
      reject(new Error('Não foi possível carregar o SDK da Meta'));
    };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  });

  return sdkLoader;
};
